// react
import React, { useMemo } from 'react';
import styled from 'styled-components';

// component
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { ServiceCard } from '@/components/atoms/ServiceCard/ServiceCard';
import { Title } from '@/components/atoms/Title/Title';

import { CardContainer } from '@/components/templates/CardContainer/CardContainer';

// hooks
import { useWindowSize } from '@/hooks/';

// store
import { Service } from '@karehero/models';

// components
import { Icons } from '@/components/atoms/Icon/icons';

// theme
import theme from '@/theme';

interface RenderServicesProps {
  services?: Service[];
  isLoading: boolean;
  onBookCall: () => void;
  onLearnMore: (slug: string) => void;
}

const iconMap: { [key: string]: Icons } = {
  bell: 'gear',
  dollar: 'sterling-sign',
  users: 'users',
  helpCircle: 'circle-question',
  activity: 'heart-pulse',
  home: 'house',
  clipboard: 'clipboard',
};

/**
 * RenderServices is a helper component that renders the the services.
 */
const RenderServices = ({
  services,
  isLoading,
  onBookCall,
  onLearnMore,
}: RenderServicesProps) => {
  // hooks/
  const [width] = useWindowSize();

  // memos
  const maxColumns = useMemo(() => {
    if (width < theme.breakpoint.lgValue) return 1;
    if (width < theme.breakpoint.xlValue) return 2;
    return 3;
  }, [width]);

  const cardSize = useMemo(() => {
    return width < theme.breakpoint.mdValue ? 'sm' : 'md';
  }, [width]);

  if (!services || services.length === 0) return null;
  return (
    <>
      <CardContainer maxColumns={maxColumns} isPreventHeightMatch>
        {services.map((service) => (
          <ServiceCard
            key={service.title}
            title={service.title}
            description1={service.description1}
            description2={service.description2}
            icon={iconMap[service.icon]}
            isLoading={isLoading}
            size={cardSize}
            onBookCall={onBookCall}
            onLearnMore={() => onLearnMore(service.slug.current)}
          />
        ))}
      </CardContainer>
    </>
  );
};

/**
 * ServiceOverview is a page that displays all of the services that
 * are available to the user.
 */
export const ServiceOverview = ({
  services,
  isLoading,
  onBookCall,
  onLearnMore,
}: ServiceOverviewProps) => {
  // memos
  const dummyServices = useMemo(() => {
    let services: Service[] = [];
    for (let i = 0; i < 6; i++) {
      services.push({
        _id: `service_${i}`,
        slug: {
          current: `service_${i}`,
          _type: 'slug',
        },
        title: `Service ${i}`,
        description1: `Description for Service ${i}`,
        description2: `Optional Description for Service ${i}`,
        icon: 'bell',
      });
    }

    return services;
  }, []);

  return (
    <StyledServiceOverview>
      <PageHeader>
        <Title isNoMargin>Care Services</Title>
        <Description>
          This is where you’ll find the services that are available to you.
        </Description>
      </PageHeader>
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <Content>
            <RenderServices
              services={dummyServices}
              isLoading
              onBookCall={onBookCall}
              onLearnMore={onLearnMore}
            />
          </Content>
        }
        content={
          <Content>
            <RenderServices
              services={services}
              isLoading={false}
              onBookCall={onBookCall}
              onLearnMore={onLearnMore}
            />
          </Content>
        }
      />
    </StyledServiceOverview>
  );
};

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[80]};
`;

const PageHeader = styled.div``;

const Content = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[32]};
  flex-wrap: wrap;
`;

const StyledServiceOverview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
  padding-bottom: ${(props) => props.theme.spacing[48]};
`;

interface ServiceOverviewProps {
  services?: Service[];
  isLoading?: boolean;
  onBookCall: () => void;
  onLearnMore: (slug: string) => void;
}

ServiceOverview.displayName = 'ServiceOverview';

export default ServiceOverview;
